import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { boardQueryOptions } from '@/entities/board'
import {
  type Employee,
  type EmployeeOverview,
  employeeProjectsQueryOptions,
  employeeQueryOptions,
  employeesOverviewQueryOptions,
  meQueryOptions,
} from '@/entities/employee'
import { projectListQueryOptions } from '@/entities/project'
import { focusStatsQueryOptions } from '@/entities/soundscape'
import { employeeTasksQueryOptions } from '@/entities/task'
import { personReportQueryOptions } from '@/entities/time-entry'
import { EmployeeDepartmentsEditor } from '@/features/assign-department'
import { PersonalBoardLink } from '@/features/open-personal-board'
import { ApiError } from '@/shared/api'
import { cn, formatDuration } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { Icon } from '@/shared/ui/icon'
import { presetRange } from '@/shared/ui/period-picker'
import { RunningTimer } from '@/shared/ui/running-timer'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { MyTasksView, type SortKey, type TaskFilter } from '@/widgets/my-tasks'

interface EmployeeSearch {
  filter?: TaskFilter
  sort?: SortKey
}

export const Route = createFileRoute('/employees_/$employeeId')({
  component: EmployeePage,
  head: () => ({ meta: [{ title: 'Сотрудник · STIT Task' }] }),
  validateSearch: (s: Record<string, unknown>): EmployeeSearch => ({
    ...(s.filter === 'in-work' ||
    s.filter === 'waiting' ||
    s.filter === 'overdue' ||
    s.filter === 'done'
      ? { filter: s.filter }
      : {}),
    ...(s.sort === 'time' || s.sort === 'updated' ? { sort: s.sort } : {}),
  }),
})

const FILTERS: { value: TaskFilter; label: string }[] = [
  { value: 'all', label: 'Всё' },
  { value: 'in-work', label: 'В работе' },
  { value: 'waiting', label: 'Ждёт ответа' },
  { value: 'overdue', label: 'Просрочено' },
  { value: 'done', label: 'Выполненные' },
]

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'relative pb-1.5 font-mono text-xs uppercase tracking-wide transition-colors',
        active ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
      )}
    >
      {children}
      {active && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 rounded-pill bg-text-primary"
        />
      )}
    </button>
  )
}

// Строка сводки времени: подпись + крупная mono-сумма.
function TimeStat({ label, seconds }: { label: string; seconds: number | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="label">{label}</span>
      <span data-numeric className="mono-num t-h3 text-text-primary">
        {seconds === undefined ? '—' : formatDuration(seconds)}
      </span>
    </div>
  )
}

function EmployeeHeader({
  employee,
  overview,
}: {
  employee: Employee
  overview: EmployeeOverview | undefined
}) {
  const { name, email } = employee
  const today = presetRange('today')
  const week = presetRange('week')
  const employeeId = employee.id
  const todayReport = useQuery({
    ...personReportQueryOptions(employeeId, today.from, today.to),
    enabled: Boolean(employeeId),
  })
  const weekReport = useQuery({
    ...personReportQueryOptions(employeeId, week.from, week.to),
    enabled: Boolean(employeeId),
  })
  const focusStats = useQuery({
    ...focusStatsQueryOptions(employeeId),
    enabled: Boolean(employeeId),
  })

  return (
    <div className="flex flex-col gap-6 rounded-card bg-bg-surface p-6 ring-1 ring-border-subtle ring-inset">
      <div className="flex flex-wrap items-start gap-4">
        <Avatar name={name} size="lg" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="t-h2 min-w-0 truncate text-text-primary">{name}</h1>
          <span className="truncate text-sm text-text-tertiary">{email}</span>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {employee.departments.length > 0 ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <DepartmentBadges names={employee.departments} />
              </span>
            ) : (
              <span className="text-xs text-text-tertiary">Без отдела</span>
            )}
            {overview?.focus && (
              <span className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span className="dot-pulse size-2 rounded-pill bg-accent" aria-hidden />В фокусе
              </span>
            )}
          </div>
        </div>
        {/* Личная доска сотрудника — его входящая корзина для задач, у которых
            ещё нет проекта. Открыта команде: закинуть коллеге можно отсюда. */}
        <PersonalBoardLink
          employeeId={employeeId}
          ariaLabel={`Личная доска: ${name}`}
          className="inline-flex shrink-0 items-center gap-2 rounded-control border border-border-strong px-3 pt-[9px] pb-[7px] font-mono text-xs font-medium uppercase tracking-wide text-text-primary transition-colors hover:border-text-primary hover:bg-bg-elevated disabled:opacity-60"
        >
          <Icon name="kanban" size={14} />
          Личная доска
        </PersonalBoardLink>
      </div>

      <EmployeeDepartmentsEditor employee={employee} />

      {overview?.timer && (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="label">Сейчас в работе</span>
          <div className="flex min-w-0 items-center gap-2">
            <RunningTimer startedAt={overview.timer.startedAt} size="sm" />
            <Link
              to="/tasks/$taskId"
              params={{ taskId: overview.timer.taskId }}
              className="min-w-0 flex-1 truncate text-sm text-text-secondary transition-colors hover:text-text-primary"
            >
              {overview.timer.taskTitle}
            </Link>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-8 border-t border-border-subtle pt-4">
        <TimeStat label="Сегодня" seconds={todayReport.data?.totalSeconds} />
        <TimeStat label="За неделю" seconds={weekReport.data?.totalSeconds} />
        <TimeStat label="Всего" seconds={overview?.totalSeconds} />
        {/* Фокус — не подмножество трекера: помодоро и режимы Endel идут и без
            взятой задачи, поэтому сумма стоит своей колонкой. Идущая сессия в
            неё не входит — сервер считает по закрытым. */}
        <TimeStat label="В фокусе" seconds={focusStats.data?.totalSeconds} />
      </div>
    </div>
  )
}

// Обратный просмотр членства: проекты, где сотрудник закреплён. Имена резолвятся по
// полному списку проектов (include_archived — членство может ссылаться на архивный).
function EmployeeProjects({ employeeId }: { employeeId: string }) {
  const projectIdsQ = useQuery(employeeProjectsQueryOptions(employeeId))
  const projectsQ = useQuery(projectListQueryOptions(true, true))

  if (projectIdsQ.isPending || projectsQ.isPending) return null

  const ids = projectIdsQ.data ?? []
  const byId = new Map((projectsQ.data ?? []).map((p) => [p.id, p]))
  const projects = ids.map((id) => byId.get(id)).filter((p) => p !== undefined)

  return (
    <section className="flex flex-col gap-2">
      <h2 className="label">Проекты</h2>
      {projects.length === 0 ? (
        <p className="text-sm text-text-tertiary">Не закреплён ни за одним проектом.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {projects.map((p) => (
            <Link
              key={p.id}
              to="/companies/$companyId"
              params={{ companyId: p.companyId }}
              className="inline-flex items-center gap-1.5 rounded-control bg-bg-surface px-2.5 py-1 text-sm text-text-secondary ring-1 ring-border-subtle transition-colors hover:text-text-primary"
            >
              <Icon name="kanban" size={13} className="text-text-tertiary" />
              <span className="max-w-[20ch] truncate">{p.name}</span>
              {p.archived && <span className="label text-text-tertiary">в архиве</span>}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

function EmployeePage() {
  const { employeeId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filter = search.filter ?? 'all'
  const sort = search.sort ?? 'due'

  const employeeQ = useQuery(employeeQueryOptions(employeeId))
  const overviewQ = useQuery(employeesOverviewQueryOptions)
  const tasksQ = useQuery(employeeTasksQueryOptions(employeeId))
  const meQ = useQuery(meQueryOptions)

  const tasks = tasksQ.data ?? []
  const boardIds = [...new Set(tasks.map((t) => t.boardId))]
  const boardResults = useQueries({ queries: boardIds.map((id) => boardQueryOptions(id)) })

  const setSearch = (next: EmployeeSearch) => void navigate({ search: next })
  const setFilter = (f: TaskFilter) =>
    setSearch({ ...(f === 'all' ? {} : { filter: f }), ...(sort !== 'due' ? { sort } : {}) })

  if (employeeQ.isPending) {
    return (
      <div className="container-x flex justify-center px-4 py-16 sm:px-6">
        <Spinner label="Загрузка профиля" />
      </div>
    )
  }

  if (employeeQ.isError) {
    const notFound = employeeQ.error instanceof ApiError && employeeQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Сотрудник не найден' : 'Не удалось загрузить профиль'}
        message={notFound ? 'Возможно, профиль удалён.' : 'Попробуйте обновить страницу.'}
      >
        <Link
          to="/employees"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К сотрудникам
        </Link>
      </StatusScreen>
    )
  }

  const employee = employeeQ.data
  const overview = overviewQ.data?.find((o) => o.employeeId === employeeId)
  const boards = boardResults.flatMap((r) => (r.data ? [r.data] : []))
  const contextPending = tasksQ.isPending || boardResults.some((r) => r.isPending)
  // Живой тик на его running-задаче (из overview). Overview отдаёт один таймер —
  // самый свежий, поэтому список здесь всегда не длиннее одного элемента.
  const running = overview?.timer
    ? [
        {
          taskId: overview.timer.taskId,
          startedAt: overview.timer.startedAt,
          taskTitle: overview.timer.taskTitle,
        },
      ]
    : []

  return (
    <div className="container-x flex flex-col gap-8 px-4 py-8 sm:px-6 sm:py-10">
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link to="/employees" className="transition-colors hover:text-text-primary">
          Сотрудники
        </Link>
        <span aria-hidden>·</span>
        <span className="text-text-secondary">{employee.name}</span>
      </nav>

      <EmployeeHeader employee={employee} overview={overview} />

      <EmployeeProjects employeeId={employeeId} />

      <section>
        <div className="mb-6 flex flex-wrap items-center gap-5 border-b border-border-subtle pb-px">
          {FILTERS.map((f) => (
            <FilterTab key={f.value} active={filter === f.value} onClick={() => setFilter(f.value)}>
              {f.label}
            </FilterTab>
          ))}
        </div>
        {tasksQ.isError ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-text-secondary">Не удалось загрузить задачи.</p>
            <Button variant="secondary" size="sm" onClick={() => void tasksQ.refetch()}>
              Повторить
            </Button>
          </div>
        ) : contextPending ? (
          <div className="flex justify-center py-12">
            <Spinner label="Загрузка задач" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="rounded-card bg-bg-surface px-6 py-10 text-center text-sm text-text-tertiary">
            У сотрудника нет назначенных задач.
          </p>
        ) : (
          <MyTasksView
            tasks={tasks}
            boards={boards}
            running={running}
            meId={meQ.data?.id}
            filter={filter}
            sort={sort}
          />
        )}
      </section>
    </div>
  )
}
